import axios, { AxiosResponse } from "axios";
import { API_BASE_URL } from "../constants/api";
import {
  mockAnalyticsData,
  mockAnalysisInsights,
  mockAnalysisPredictions,
  mockAnalysisModels,
  mockAnalysisResponse,
} from "../mocks/data";

// Helper function to simulate API delay
const simulateApiDelay = () =>
  new Promise((resolve) => setTimeout(resolve, 300));

// Helper function to check if backend is available
const isBackendAvailable = async () => {
  try {
    await axios.get(`${API_BASE_URL}/health`, { timeout: 2000 });
    return true;
  } catch {
    return false;
  }
};

// Analysis API endpoints
export const analysisApi = {
  // Get business analytics overview
  getBusinessAnalytics: async (params?: {
    dateRange?: [Date, Date];
    groupBy?: "day" | "week" | "month";
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/business`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: {
        total_sales: mockAnalyticsData.totalSales,
        total_revenue: mockAnalyticsData.totalRevenue,
        average_order_value: mockAnalyticsData.averageOrderValue,
        total_expenses: mockAnalyticsData.totalExpenses,
        profit_margin: mockAnalyticsData.profitMargin,
        top_selling_items: mockAnalyticsData.topSellingItems,
        low_stock_items: mockAnalyticsData.lowStockItems,
        recipe_performance: mockAnalyticsData.recipePerformance,
        sales_by_payment_method: mockAnalyticsData.salesByPaymentMethod,
        sales_by_order_type: mockAnalyticsData.salesByOrderType,
        stock_movements: mockAnalyticsData.stockMovements,
        stock_levels: mockAnalyticsData.stockLevels,
      },
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get sales analytics
  getSalesAnalytics: async (params?: {
    dateRange?: [Date, Date];
    groupBy?: "day" | "week" | "month";
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/sales`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: {
        total_sales: mockAnalyticsData.totalSales,
        total_revenue: mockAnalyticsData.totalRevenue,
        average_order_value: mockAnalyticsData.averageOrderValue,
        top_selling_items: mockAnalyticsData.topSellingItems,
        sales_by_payment_method: mockAnalyticsData.salesByPaymentMethod,
        sales_by_order_type: mockAnalyticsData.salesByOrderType,
      },
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get inventory analytics
  getInventoryAnalytics: async (params?: {
    dateRange?: [Date, Date];
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/inventory`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: {
        low_stock_items: mockAnalyticsData.lowStockItems,
        stock_movements: mockAnalyticsData.stockMovements,
        stock_levels: mockAnalyticsData.stockLevels,
      },
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get recipe analytics
  getRecipeAnalytics: async (params?: {
    dateRange?: [Date, Date];
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/recipes`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: {
        recipe_performance: mockAnalyticsData.recipePerformance,
      },
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get AI-powered insights
  getAIInsights: async (params?: {
    dateRange?: [Date, Date];
    insight_type?: "sales" | "inventory" | "recipe" | "general";
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/ai-insights`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: mockAnalysisInsights,
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get predictive analytics
  getPredictiveAnalytics: async (params?: {
    dateRange?: [Date, Date];
    prediction_type?: "sales" | "inventory" | "demand";
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/predictive`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: mockAnalysisPredictions,
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Export analytics data
  exportAnalytics: async (params?: {
    dateRange?: [Date, Date];
    format?: "csv" | "excel" | "pdf";
    report_type?: "sales" | "inventory" | "recipe" | "comprehensive";
  }): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/export`, { params });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: {
        download_url: "/api/analysis/export/download/mock-report.csv",
        filename: `analytics-report-${
          new Date().toISOString().split("T")[0]
        }.csv`,
        format: params?.format || "csv",
        report_type: params?.report_type || "comprehensive",
      },
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get available AI models
  getAvailableModels: async (): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.get(`${API_BASE_URL}/analysis/models`);
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    return {
      data: mockAnalysisModels,
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Send message to AI
  sendMessage: async (
    message: string,
    modelName: string = "gpt2"
  ): Promise<AxiosResponse<any>> => {
    try {
      const isAvailable = await isBackendAvailable();
      if (isAvailable) {
        return axios.post(`${API_BASE_URL}/analysis/chat/${modelName}`, {
          message,
        });
      }
    } catch (error) {
      console.log("Backend not available, using mock data");
    }

    // Use mock data
    await simulateApiDelay();

    const response = {
      ...mockAnalysisResponse,
      message: `AI analysis for: "${message}". Based on the current data, I can provide insights about sales trends, inventory optimization, and business performance.`,
      model_used: modelName,
    };

    return {
      data: response,
      status: 200,
      statusText: "OK",
      headers: {},
      config: {} as any,
    } as AxiosResponse<any>;
  },

  // Get available models (alias for getAvailableModels)
  getModels: async (): Promise<AxiosResponse<any>> => {
    return analysisApi.getAvailableModels();
  },
};
